import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  DnsRecordDto,
  DnsZoneDto,
  UpsertDnsRecordRequest
} from "@/types/api";

/**
 * Live DNS zone for a container — read + record CRUD. Every mutation
 * invalidates the zone so the table re-renders with the latest state.
 *
 * 1-min stale window: DNS edits are uncommon, refetching on tab focus
 * would beat up the 20i API for no real benefit.
 */
export function useDnsZone(containerId: number | undefined) {
  return useQuery({
    queryKey: ["dns", containerId],
    enabled: typeof containerId === "number",
    queryFn: ({ signal }) =>
      api.get<DnsZoneDto>(`/api/containers/${containerId}/dns`, signal),
    staleTime: 60_000
  });
}

export function useAddDnsRecord(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertDnsRecordRequest) =>
      api.post<DnsRecordDto>(`/api/containers/${containerId}/dns`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", containerId] });
    }
  });
}

export function useUpdateDnsRecord(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertDnsRecordRequest }) =>
      api.patch(`/api/containers/${containerId}/dns/${encodeURIComponent(id)}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", containerId] });
    }
  });
}

export function useDeleteDnsRecord(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/containers/${containerId}/dns/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", containerId] });
    }
  });
}
